
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Clock, Calendar } from 'lucide-react';

interface ShiftSignupProps {
  date: Date;
  onShiftRequest: (startTime: Date, endTime: Date, notes: string) => void;
}

const ShiftSignup: React.FC<ShiftSignupProps> = ({ date, onShiftRequest }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [notes, setNotes] = useState('');
  const { user } = useAuth();

  const handleSubmit = () => {
    // Create date objects with the selected times
    const start = new Date(date);
    const [startHour, startMinute] = startTime.split(':').map(Number);
    start.setHours(startHour, startMinute, 0, 0);

    const end = new Date(date);
    const [endHour, endMinute] = endTime.split(':').map(Number);
    end.setHours(endHour, endMinute, 0, 0);

    // Validate times
    if (start >= end) {
      toast.error('Eindtijd moet later zijn dan starttijd');
      return;
    }

    // Submit shift request
    onShiftRequest(start, end, notes);
    setIsOpen(false);

    // Reset form
    setStartTime('09:00');
    setEndTime('17:00');
    setNotes('');
  };

  if (!user) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-full">
          <Calendar className="h-4 w-4 mr-2" />
          Beschikbaarheid opgeven
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Beschikbaarheid opgeven voor {format(date, 'EEEE d MMMM', { locale: nl })}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime">Start tijd</Label>
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime">Eind tijd</Label>
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notities</Label>
            <Input
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Bijv. alleen beschikbaar voor deze uren"
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Annuleren
          </Button>
          <Button onClick={handleSubmit}>
            Versturen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftSignup;
